import React, { useMemo } from "react";
import styled from "styled-components";
import { FiDatabase, FiInfo } from "react-icons/fi";
import {
  Empty, Group, Section, Widget, WidgetGrid, tokens,
} from "../../activity-logs/styles";
import { IOS } from "../../activity-logs/labels";
import { Notice, StorageBar, Tag } from "../../creditos/styles";
import { formatCompact, formatCurrency, formatPercent } from "../../creditos/dashboardData";
import { hairline, surface } from "../../../styles/designTokens";
import { RateDumbbell } from "../dashboard/ReformaDashboard";
import { formatTimes } from "../dashboard/reformaData";
import { buildCredits } from "./dadosData";
import { AnimatedMoney as MoneyValue, Num } from "../components/Motion";

/**
 * Créditos e despesas: a mesma base de despesas gera mais crédito no regime futuro porque
 * a alíquota sobe. Por isso a peça central compara os dois regimes na mesma escala. Azul é
 * o crédito de hoje e verde é o crédito futuro; os demais números ficam neutros.
 */

const TODAY = IOS.blue;
const FUTURE = IOS.green;
const STATUS_COLORS = { success: IOS.green, warning: IOS.orange, danger: IOS.red };
const GROUP_COLORS = [IOS.blue, IOS.purple, IOS.teal, IOS.gray];
const label2 = ({ theme }) => tokens.label2(theme);
const label3 = ({ theme }) => tokens.label3(theme);
const dot = (color) => <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, opacity: 1 }} />;
const rate = (v) => `${(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;

const Bridge = styled.div`
  flex: 1;
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;

  .row, .gain { display: grid; grid-template-columns: 118px minmax(0, 1fr) 96px; gap: 14px; align-items: center; }
  .who { font-size: 1.05em; font-weight: 600; letter-spacing: -0.03em; }
  .who small { display: block; margin-top: 2px; font-size: 0.72em; font-weight: 400; letter-spacing: 0; color: ${label2}; }
  .track { position: relative; display: flex; gap: 2px; height: 38px; border-radius: 9px; background: ${({ theme }) => surface(theme, 1)}; }
  .part {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 3px;
    overflow: hidden;
    color: #fff;
    font-size: 0.74em;
    font-weight: 600;
    letter-spacing: -0.01em;
    white-space: nowrap;
  }
  .part:first-child { border-radius: 9px 0 0 9px; }
  .part:last-of-type { border-radius: 0 9px 9px 0; }
  .part:only-of-type { border-radius: 9px; }
  /* Até onde o crédito de hoje chega, marcado também na barra de baixo */
  .mark { position: absolute; top: -6px; bottom: -6px; width: 1px; background: ${({ theme }) => tokens.label(theme)}; opacity: 0.55; }
  .v { text-align: right; white-space: nowrap; font-size: 1.02em; font-weight: 600; letter-spacing: -0.03em; }

  .span { position: relative; height: 30px; }
  .bracket { position: absolute; top: 3px; height: 7px; border: 1px solid ${FUTURE}; border-top: none; border-radius: 0 0 4px 4px; opacity: 0.8; }
  .gainText { position: absolute; top: 13px; transform: translateX(-50%); font-size: 0.76em; font-weight: 500; white-space: nowrap; color: ${({ theme }) => theme.text}; }
  .gainText b { font-weight: 650; color: ${FUTURE}; }

  @media (max-width: 640px) {
    .row, .gain { grid-template-columns: 76px minmax(0, 1fr) 78px; gap: 8px; }
    .part { font-size: 0; }
  }
`;

const Facts = styled.dl`
  margin: auto 0 0;
  padding-top: 12px;
  display: grid;
  grid-template-columns: 1fr auto;
  font-size: 0.82em;
  dt, dd { margin: 0; padding: 8px 0; border-top: 1px solid ${({ theme }) => hairline(theme)}; }
  dt { color: ${label2}; }
  dd { font-weight: 600; letter-spacing: -0.02em; text-align: right; white-space: nowrap; }
`;

const SUMMARY_COLUMNS = "minmax(0, 1fr) repeat(3, 120px) 128px";

const SummaryHead = styled.div`
  display: grid;
  grid-template-columns: ${SUMMARY_COLUMNS};
  gap: 12px;
  margin-top: 14px;
  padding-bottom: 7px;
  font-size: 0.7em;
  color: ${label3};
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  > :not(:first-child) { text-align: right; }
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: ${SUMMARY_COLUMNS};
  gap: 12px;
  align-items: center;
  min-height: 56px;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};

  .name { min-width: 0; font-size: 0.86em; font-weight: 500; letter-spacing: -0.012em; }
  .name > span { font-size: 1em; color: ${({ theme }) => theme.text}; font-weight: 500; }
  .name small { display: block; margin-top: 3px; font-size: 0.88em; font-weight: 300; letter-spacing: 0; line-height: 1.4; color: ${label2}; }
  .n { text-align: right; white-space: nowrap; font-size: 0.84em; color: ${label2}; }
  .end { text-align: right; white-space: nowrap; font-size: 0.92em; font-weight: 600; letter-spacing: -0.025em; }
  &.total { border-bottom: none; min-height: 48px; padding-bottom: 0; }
  &.total .name { font-weight: 600; }
  &.total .n { color: ${({ theme }) => theme.text}; font-weight: 600; letter-spacing: -0.02em; }

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr) 110px;
    .n { display: none; }
  }
`;

const HeadScreen = styled.div`
  @media (max-width: 900px) { display: none; }
`;

const accountColumns = (hasCofins) => `44px minmax(180px, 1.5fr) minmax(90px, 1fr) 132px 132px${hasCofins ? " 132px" : ""}`;

const AccountsScroll = styled.div`
  overflow-x: auto;
  scrollbar-width: thin;
  margin-top: 14px;
`;

const Accounts = styled.div`
  min-width: ${({ $wide }) => ($wide ? 860 : 720)}px;
  font-variant-numeric: tabular-nums;

  .line {
    display: grid;
    grid-template-columns: ${({ $wide }) => accountColumns($wide)};
    gap: 12px;
    align-items: center;
    border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  }
  .line.head { padding-bottom: 7px; font-size: 0.7em; color: ${label3}; }
  .line.item { min-height: 52px; padding: 6px 0; }
  .line.total { min-height: 46px; border-bottom: none; }
  .right { text-align: right; }
  .code { font-size: 0.8em; color: ${label3}; }
  .name { min-width: 0; font-size: 0.86em; font-weight: 500; letter-spacing: -0.012em; line-height: 1.3; }
  .name small { display: block; margin-top: 3px; font-size: 0.88em; font-weight: 400; letter-spacing: 0; }
  .share { display: grid; grid-template-columns: minmax(0, 1fr) 42px; gap: 8px; align-items: center; }
  .share .bar { height: 4px; border-radius: 2px; background: ${({ theme }) => surface(theme, 2)}; }
  .share i { display: block; height: 100%; min-width: 3px; border-radius: 2px; background: ${FUTURE}; opacity: 0.85; }
  .share span { text-align: right; font-size: 0.76em; color: ${label2}; }
  .n { text-align: right; white-space: nowrap; font-size: 0.84em; color: ${label2}; }
  .end { text-align: right; white-space: nowrap; font-size: 0.92em; font-weight: 600; letter-spacing: -0.025em; }
  .line.total .name { font-weight: 600; }
  .line.total .n { font-weight: 600; letter-spacing: -0.02em; color: ${({ theme }) => theme.text}; }
`;

const GroupLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: 8px;
  font-size: 0.76em;
  b { margin-left: 4px; font-weight: 600; color: ${({ theme }) => theme.text}; }
`;

function introText(isReal) {
  if (isReal) {
    return "Base de crédito extraída dos registros M105 (PIS) e M505 (COFINS) do SPED Contribuições, por natureza da despesa. No regime futuro, essas naturezas passam a gerar crédito de cerca de 26,5% (CBS 8,8% + IBS 17,7%). No comparativo com o regime atual, PIS (1,65%) e COFINS (7,60%) usam cada um a própria base real.";
  }
  return "Base de crédito extraída do registro M105 do SPED Contribuições, por natureza da despesa. No regime futuro, essas naturezas passam a gerar crédito de cerca de 26,5% (CBS 8,8% + IBS 17,7%). O comparativo com o regime atual ainda usa 9,25% aproximado sobre essa mesma base, porque o registro M505 (base real de COFINS) não foi processado. Reprocessar o SPED Contribuições habilita o cálculo com a base real de cada contribuição.";
}

function BridgeWidget({ d }) {
  const max = Math.max(d.today, d.future, 1);
  const pct = (v) => (v / max) * 100;
  const hasTodaySplit = d.isReal && d.detail.creditoPis != null && d.detail.creditoCofins != null;
  const todayParts = hasTodaySplit
    ? [{ key: "PIS", value: d.detail.creditoPis, opacity: 0.6 }, { key: "COFINS", value: d.detail.creditoCofins, opacity: 0.9 }]
    : [{ key: `PIS/COFINS ${rate(d.rates.today)}`, value: d.today, opacity: 0.85 }];
  const futureParts = [
    { key: `CBS ${rate(d.rates.cbs)}`, value: d.cbs, opacity: 0.74 },
    { key: `IBS ${rate(d.rates.ibs)}`, value: d.ibs, opacity: 0.9 },
  ];
  const gainLeft = pct(d.today);
  const gainWidth = Math.max(0, pct(d.future) - gainLeft);
  return (
    <Widget $span={3} $delay={200} style={{ display: "flex", flexDirection: "column" }}>
      <div className="w-title">Do crédito atual ao futuro</div>
      <div className="w-sub">A mesma base de <Num value={d.base} format={formatCompact} /> na mesma escala. O que muda é a alíquota de crédito</div>
      <Bridge>
        <div className="row">
          <div className="who">Hoje<small><Num value={d.base} format={formatCompact} /> × <Num value={d.rates.today} format={rate} /></small></div>
          <div className="track" role="img" aria-label={`Crédito de hoje: ${formatCompact(d.today)}`}>
            {todayParts.map((p) => (
              <span key={p.key} className="part" title={`${p.key}: ${formatCurrency(p.value)}`} style={{ width: `${pct(p.value)}%`, background: TODAY, opacity: p.opacity }}>{p.key}</span>
            ))}
          </div>
          <div className="v" title={d.captions.today || formatCurrency(d.today)}><Num value={d.today} format={formatCompact} /></div>
        </div>
        <div className="row">
          <div className="who">2033<small><Num value={d.base} format={formatCompact} /> × <Num value={d.rates.future} format={rate} /></small></div>
          <div className="track" role="img" aria-label={`Crédito em 2033: ${formatCompact(d.future)}`}>
            {futureParts.map((p) => (
              <span key={p.key} className="part" title={`${p.key}: ${formatCurrency(p.value)}`} style={{ width: `${pct(p.value)}%`, background: FUTURE, opacity: p.opacity }}>{p.key}</span>
            ))}
            <span className="mark" style={{ left: `${gainLeft}%` }} />
          </div>
          <div className="v" title={d.captions.future || formatCurrency(d.future)}><Num value={d.future} format={formatCompact} /></div>
        </div>
        {d.gain > 0 && (
          <div className="gain">
            <span />
            <div className="span">
              <span className="bracket" style={{ left: `${gainLeft}%`, width: `${gainWidth}%` }} />
              <span className="gainText" style={{ left: `${gainLeft + gainWidth / 2}%` }} title={d.captions.gain}><b>+<Num value={d.gain} format={formatCompact} /></b> de crédito adicional</span>
            </div>
            <span />
          </div>
        )}
      </Bridge>
    </Widget>
  );
}

function RateWidget({ d }) {
  const scale = Math.max(30, Math.ceil(d.rates.future / 5) * 5);
  return (
    <Widget $delay={250} style={{ display: "flex", flexDirection: "column" }}>
      <div className="w-title">Alíquota de crédito</div>
      <div className="w-sub">Sobre a mesma base de despesas</div>
      <div style={{ marginTop: 18 }}>
        <RateDumbbell from={d.rates.today} to={d.rates.future} color={FUTURE} scale={scale} format={rate} />
      </div>
      <div style={{ marginTop: 12, fontSize: "0.78em" }}>
        <Tag $color={d.isReal ? IOS.green : IOS.gray}>{d.isReal ? "Hoje: base real (M105 e M505)" : "Hoje: estimado, M505 não processado"}</Tag>
      </div>
      <Facts>
        {d.isReal && d.detail.creditoPis != null && (
          <>
            <dt>PIS sobre <Num value={d.detail.basePis} format={formatCompact} /></dt>
            <dd title={formatCurrency(d.detail.creditoPis)}><Num value={d.detail.creditoPis} format={formatCompact} /></dd>
            <dt>COFINS sobre <Num value={d.detail.baseCofins} format={formatCompact} /></dt>
            <dd title={formatCurrency(d.detail.creditoCofins)}><Num value={d.detail.creditoCofins} format={formatCompact} /></dd>
          </>
        )}
        <dt>CBS</dt><dd><Num value={d.rates.cbs} format={rate} /></dd>
        <dt>IBS</dt><dd><Num value={d.rates.ibs} format={rate} /></dd>
        <dt>Crédito futuro</dt><dd><Num value={d.multiplier} format={formatTimes} /> o atual</dd>
      </Facts>
    </Widget>
  );
}

function SummaryWidget({ d }) {
  return (
    <Widget $delay={300}>
      <div className="w-title">Potencial de crédito CBS e IBS sobre despesas</div>
      <div className="w-sub">Por classificação da despesa</div>
      <HeadScreen>
        <SummaryHead aria-hidden="true">
          <span>Classificação</span>
          <span>Base de crédito</span>
          <span>Crédito CBS</span>
          <span>Crédito IBS</span>
          <span>Total CBS+IBS</span>
        </SummaryHead>
      </HeadScreen>
      {d.summary.map((r) => (
        <SummaryRow key={r.classificacao}>
          <div className="name">
            <Tag $color={STATUS_COLORS[r.status] || IOS.gray}>{r.label}</Tag>
            <small>{r.descricao}</small>
          </div>
          <div className="n" title={formatCurrency(r.valorAnual)}>{formatCurrency(r.valorAnual)}</div>
          <div className="n" title={formatCurrency(r.creditoCBS)}>{formatCurrency(r.creditoCBS)}</div>
          <div className="n" title={formatCurrency(r.creditoIBS)}>{formatCurrency(r.creditoIBS)}</div>
          <div className="end" title={formatCurrency(r.total)}>{formatCurrency(r.total)}</div>
        </SummaryRow>
      ))}
      <SummaryRow className="total">
        <div className="name">{d.summaryTotal.label}</div>
        <div className="n">{formatCurrency(d.summaryTotal.valorAnual)}</div>
        <div className="n">{formatCurrency(d.summaryTotal.creditoCBS)}</div>
        <div className="n">{formatCurrency(d.summaryTotal.creditoIBS)}</div>
        <div className="end">{formatCurrency(d.summaryTotal.total)}</div>
      </SummaryRow>
    </Widget>
  );
}

function AccountsWidget({ d }) {
  const hasCofins = d.isReal;
  const colorOf = (group) => GROUP_COLORS[Math.max(0, d.groups.findIndex((g) => g.label === group)) % GROUP_COLORS.length];
  return (
    <Widget $delay={350}>
      <div className="w-title">Naturezas de crédito</div>
      <div className="w-sub">Detalhe do SPED {d.isReal ? "M105 e M505" : "M105"}, da maior para a menor</div>
      {d.groups.length > 0 && (
        <>
          <StorageBar aria-hidden="true" style={{ marginTop: 14 }}>
            {d.groups.map((g, i) => <span key={g.label} style={{ flexGrow: g.value, background: GROUP_COLORS[i % GROUP_COLORS.length] }} />)}
          </StorageBar>
          <GroupLegend>
            {d.groups.map((g, i) => (
              <Tag key={g.label} $color={GROUP_COLORS[i % GROUP_COLORS.length]} title={formatCurrency(g.value)}>
                {g.label}<b><Num value={g.share} format={formatPercent} /></b>
              </Tag>
            ))}
          </GroupLegend>
        </>
      )}
      <AccountsScroll>
        <Accounts $wide={hasCofins}>
          <div className="line head" aria-hidden="true">
            <span>Código</span>
            <span>Natureza e agrupamento</span>
            <span>Participação</span>
            <span className="right">Saldo (PIS · M105)</span>
            {hasCofins && <span className="right">Base COFINS (M505)</span>}
            <span className="right">Crédito CBS+IBS</span>
          </div>
          {d.accounts.map((c) => (
            <div className="line item" key={c.codigo}>
              <span className="code">{c.codigo}</span>
              <div className="name">
                {c.conta}
                <small><Tag $color={colorOf(c.group)}>{c.group}</Tag></small>
              </div>
              <div className="share" title={formatPercent(c.share)}>
                <span className="bar"><i style={{ width: `${c.share}%` }} /></span>
                <span><Num value={c.share} format={formatPercent} /></span>
              </div>
              <div className="n">{formatCurrency(c.saldo2025)}</div>
              {hasCofins && <div className="n">{c.baseCofins == null ? "—" : formatCurrency(c.baseCofins)}</div>}
              <div className="end">{formatCurrency(c.creditoCbsIbs)}</div>
            </div>
          ))}
          <div className="line total">
            <span />
            <div className="name">Total das naturezas</div>
            <span />
            <div className="n">{formatCurrency(d.accountsTotal.saldo)}</div>
            {hasCofins && <div className="n">{formatCurrency(d.accountsTotal.baseCofins)}</div>}
            <div className="end">{formatCurrency(d.accountsTotal.credit)}</div>
          </div>
        </Accounts>
      </AccountsScroll>
    </Widget>
  );
}

export default function CreditsDashboard({ fiscalData, companyFiltered }) {
  const d = useMemo(() => buildCredits(fiscalData.creditos), [fiscalData.creditos]);

  // Falha parcial: os dados mensais podem existir sem os de crédito
  if (!d) {
    return (
      <Section $first>
        <Group>
          <Empty>
            <div className="e-icon"><FiDatabase /></div>
            <div className="e-title">Sem dados de crédito para estes filtros</div>
            <div className="e-sub">O registro M105 do SPED Contribuições não trouxe despesas com direito a crédito.</div>
          </Empty>
        </Group>
      </Section>
    );
  }

  return (
    <>
      <Section $first>
        {companyFiltered && (
          <Notice style={{ marginTop: 0, marginBottom: 12 }}>
            <FiInfo />
            <span>Com empresa selecionada, as despesas (M105) continuam sendo do projeto inteiro.</span>
          </Notice>
        )}
        <WidgetGrid>
          <Widget $delay={0}>
            <div className="w-head">{dot(IOS.gray)} Base de crédito</div>
            <div className="w-value"><MoneyValue value={d.base} /></div>
            <div className="w-foot">Despesas elegíveis · SPED M105</div>
          </Widget>
          <Widget $delay={50} title={d.captions.today}>
            <div className="w-head">{dot(TODAY)} Crédito atual (PIS/COFINS)</div>
            <div className="w-value"><MoneyValue value={d.today} /></div>
            <div className="w-foot"><Num value={d.rates.today} format={rate} /> × base{d.isReal ? "" : ", estimado"}</div>
          </Widget>
          <Widget $delay={100} title={d.captions.future}>
            <div className="w-head">{dot(FUTURE)} Crédito futuro (CBS+IBS)</div>
            <div className="w-value"><MoneyValue value={d.future} /></div>
            <div className="w-foot"><Num value={d.rates.future} format={rate} /> × base</div>
          </Widget>
          <Widget $delay={150} title={d.captions.gain}>
            <div className="w-head">{dot(FUTURE)} Ganho adicional de crédito</div>
            <div className="w-value"><MoneyValue value={d.gain} /></div>
            <div className="w-foot">Futuro − atual · <Num value={d.multiplier} format={formatTimes} /> o crédito de hoje</div>
          </Widget>
        </WidgetGrid>
        <Notice>
          <FiInfo />
          <span>{introText(d.isReal)}</span>
        </Notice>
      </Section>

      <Section $delay={80}>
        <WidgetGrid>
          <BridgeWidget d={d} />
          <RateWidget d={d} />
        </WidgetGrid>
      </Section>

      <Section $delay={120}>
        <SummaryWidget d={d} />
      </Section>

      <Section $delay={160}>
        <AccountsWidget d={d} />
      </Section>
    </>
  );
}
